import {StockItem} from 'src/app/shared-utilities/models-old/datastructures';
import {IAutoOrderOrderItem, IAutoOrderOrderProgressStep, IAutoOrderOrderStockItem} from '../models';
import {decodeOnHoldCode} from '../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';

//#region METHOD countAutoOrderCompletedSteps

/**
 * ### countAutoOrderCompletedSteps
 * Counts the number of completed steps (`true`) in the auto order progress step object.
 *
 * @function
 * @param {IAutoOrderOrderProgressStep} steps - The current step progress flags.
 * @returns {number} The number of steps marked as complete (`true`).
 *
 * @example
 * const completed = countAutoOrderCompletedSteps(progress.steps);
 * console.info(`Steps done: ${completed}`);
 */
export function countAutoOrderCompletedSteps(steps: IAutoOrderOrderProgressStep): number {
  return Object.values(steps).filter((step: boolean) => step === true).length;
}

//#endregion

//#region METHOD countAutoOrderTotalSteps

/**
 * ### countAutoOrderTotalSteps
 * Calculates the total number of steps being tracked in the auto order process.
 *
 * @function
 * @param {IAutoOrderOrderProgressStep} steps - The current step progress flags.
 * @returns {number} Total number of steps.
 *
 * @example
 * const total = countAutoOrderTotalSteps(progress.steps);
 * console.info(`Total steps: ${total}`);
 */
export function countAutoOrderTotalSteps(steps: IAutoOrderOrderProgressStep): number {
  return Object.values(steps).length;
}

//#endregion

//#region FUNCTION groupOrderItemsByCode

/**
 * Groups a 2D array of `StockItem` objects based on the `code` property.
 *
 * - Case-sensitive comparison is used for the `code` field.
 * - One representative from each duplicated `code` is included in `uniques`.
 * - Remaining duplicates are stored in `duplicates`.
 * - Items with `null` or `undefined` `code` are collected separately.
 *
 * @param {StockItem[][]} orderItems - A 2D array of `StockItem` objects to group.
 *
 * @returns {{
 *   duplicates: StockItem[];
 *   uniques: StockItem[];
 *   nullOrUndefined: StockItem[];
 * }} An object containing:
 * - `duplicates`: Items sharing the same `code` (excluding the first occurrence).
 * - `uniques`: All unique `code` items plus one instance of any duplicates.
 * - `nullOrUndefined`: Items with a `null` or `undefined` `code`.
 */
export function groupOrderItemsByCode(orderItems: StockItem[][]): {
  duplicates: StockItem[];
  uniques: StockItem[];
  nullOrUndefined: StockItem[];
} {
  const flattened = orderItems.flat();
  const codeMap = new Map<string, StockItem[]>();
  const nullOrUndefined: StockItem[] = [];

  for (const item of flattened) {
    const code = item.code;
    if (code === null || code === undefined) {
      nullOrUndefined.push(item);
    } else {
      if (!codeMap.has(code)) {
        codeMap.set(code, []);
      }
      codeMap.get(code)?.push(item);
    }
  }

  const uniques: StockItem[] = [];
  const duplicates: StockItem[] = [];

  for (const items of codeMap.values()) {
    if (items.length === 1) {
      uniques.push(items[0]);
    } else {
      uniques.push(items[0]);
      duplicates.push(...items.slice(1));
    }
  }

  return {
    uniques,
    duplicates,
    nullOrUndefined,
  };
}

//#endregion

//#region FUNCTION determineMissingStockCodes
/**
 * Identifies and categorizes order items that are missing corresponding stock items,
 * while enriching matched stock items with display-related flags.
 *
 * @param {IAutoOrderOrderItem[]} orderItems - The order items to verify against stock item availability.
 * @param {IAutoOrderOrderStockItem[]} stockItems - The available stock items to match with order items using the `code` property.
 *
 * @returns {{
 *   stockItemMissingNewAdded: IAutoOrderOrderItem[],
 *   stockItemMissingNewPeriod2: IAutoOrderOrderItem[],
 *   stockItemMissingNewTrue: IAutoOrderOrderItem[],
 *   stockItemMissingNewFalse: IAutoOrderOrderItem[],
 *   validOrderItems: IAutoOrderOrderItem[],
 *   validOrderItemsStockItems: IAutoOrderOrderStockItem[],
 * }} An object containing:
 * - `stockItemMissingNewAdded`: Order items with `new === 'ADDED'` and no matching stock item.
 * - `stockItemMissingNewPeriod2`: Order items with `new === 'PERIOD-2'` and no matching stock item.
 * - `stockItemMissingNewTrue`: Order items with `new === true` and no matching stock item.
 * - `stockItemMissingNewFalse`: Order items with `new === false` and no matching stock item.
 * - `validOrderItems`: Order items that have a matching stock item.
 * - `validOrderItemsStockItems`: Matched stock items, enriched with `flagged` and `poOnHold` flags.
 *
 * @remarks
 * - Matching is done by exact string comparison on the `code` property.
 * - Stock items with the `_tags` array containing `'ao_flag'` will be marked with `flagged: true`.
 * - Stock items with an `onHoldCode` that decodes to include `'Purchase Orders'` will be marked with `poOnHold: true`.
 * - This function helps drive visual feedback and conditional logic in AO (Auto Order) workflows.
 */
export function determineAutoOrderMissingStockCodes(
  orderItems: IAutoOrderOrderItem[],
  stockItems: IAutoOrderOrderStockItem[],
): {
  stockItemMissingNewAdded: IAutoOrderOrderItem[],
  stockItemMissingNewPeriod2: IAutoOrderOrderItem[],
  stockItemMissingNewTrue: IAutoOrderOrderItem[],
  stockItemMissingNewFalse: IAutoOrderOrderItem[],
  validOrderItems: IAutoOrderOrderItem[],
  validOrderItemsStockItems: IAutoOrderOrderItem[],
} {
  const stockItemMissingNewAdded: IAutoOrderOrderItem[] = [] as IAutoOrderOrderItem[];
  const stockItemMissingNewPeriod2: IAutoOrderOrderItem[] = [] as IAutoOrderOrderItem[];
  const stockItemMissingNewTrue: IAutoOrderOrderItem[] = [] as IAutoOrderOrderItem[];
  const stockItemMissingNewFalse: IAutoOrderOrderItem[] = [] as IAutoOrderOrderItem[];
  const validOrderItems: IAutoOrderOrderItem[] = [] as IAutoOrderOrderItem[];
  const validOrderItemsStockItems: IAutoOrderOrderStockItem[] = [] as IAutoOrderOrderStockItem[];

  // Loop through the orderItems and check if there is a valid stock item for this code
  // If there is no stock item -> stock item is missing
  // else valid stock item and valid code
  // stock items missing means that the stock item is not linked to this supplier hence stockItemMissing

  orderItems.forEach((item: IAutoOrderOrderItem) => {
    const stockItemFound: IAutoOrderOrderStockItem | undefined = stockItems.find(
      (stockItem: IAutoOrderOrderStockItem) => stockItem.code === item.code,
    );
    if (stockItemFound) {
      const stockItemFoundInfo: IAutoOrderOrderStockItem = {...stockItemFound};
      stockItemFoundInfo.flagged = !!stockItemFound._tags?.includes('ao_flag');
      stockItemFoundInfo.poOnHold = !!decodeOnHoldCode(stockItemFound.onHoldCode)
        .includes('Purchase Orders');
      validOrderItems.push(item);
      validOrderItemsStockItems.push(stockItemFoundInfo);
    } else {
      switch (item.new) {
        case 'ADDED':
          stockItemMissingNewAdded.push(item);
          break;
        case 'PERIOD-2':
          stockItemMissingNewPeriod2.push(item);
          break;
        case true:
          stockItemMissingNewTrue.push(item);
          break;
        case false:
          stockItemMissingNewFalse.push(item);
          break;
      }
    }
  });
  return {
    stockItemMissingNewAdded,
    stockItemMissingNewPeriod2,
    stockItemMissingNewTrue,
    stockItemMissingNewFalse,
    validOrderItems,
    validOrderItemsStockItems,
  };
}

//#endregion

//#region FUNCTION determineMissingAutoOrderItemCodes
/**
 * Identifies stock items that do not have corresponding order items and categorizes valid matches.
 *
 * @param {IAutoOrderOrderItem[]} orderItems - The list of order items to match against stock item codes.
 * @param {IAutoOrderOrderStockItem[]} stockItems - The list of stock items to validate for corresponding order items.
 *
 * @returns {{
 *   orderItemMissing: IAutoOrderOrderStockItem[],
 *   validStockItems: IAutoOrderOrderStockItem[],
 *   validStockItemsOrderItems: IAutoOrderOrderItem[],
 * }} An object containing:
 * - `orderItemMissing`: Stock items for which no matching order item exists.
 * - `validStockItems`: Stock items that have a matching order item by code.
 * - `validStockItemsOrderItems`: Order items that correspond to the matched `validStockItems`.
 *
 * @remarks
 * - Matching is based on an exact string match of the `code` property.
 * - This function complements `determineAutoOrderMissingStockCodes`, operating from the stock perspective.
 * - Useful for highlighting orphaned stock entries and ensuring consistency between order data and inventory.
 */
export function determineMissingAutoOrderItemCodes(
  orderItems: IAutoOrderOrderItem[],
  stockItems: IAutoOrderOrderStockItem[],
): {
  orderItemMissing: IAutoOrderOrderStockItem[],
  validStockItems: IAutoOrderOrderStockItem[],
  validStockItemsOrderItems: IAutoOrderOrderItem[],
} {

  // Loop through the stockItems and check if there is a valid order item for this code
  // If there is no order item -> order item is missing
  // else valid order item and valid stock item
  // Missing order items means that there is not a valid order item linked to the stock item,

  const orderItemMissing: IAutoOrderOrderStockItem[] = [];
  const validStockItems: IAutoOrderOrderStockItem[] = [];
  const validStockItemsOrderItems: IAutoOrderOrderItem[] = [];

  stockItems.forEach((item: IAutoOrderOrderStockItem) => {
    const orderItemFound: IAutoOrderOrderItem | undefined = orderItems.find(
      (orderItem: IAutoOrderOrderItem) => orderItem.code === item.code);
    if (orderItemFound) {
      validStockItems.push(item);
      validStockItemsOrderItems.push(orderItemFound);
    } else {
      orderItemMissing.push(item);
    }
  });

  return {
    orderItemMissing,
    validStockItems,
    validStockItemsOrderItems,
  };
}

//#endregion
